import { type FC, useState, useEffect, useMemo } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useShikiHighlighter } from "@/hooks/useShikiHighlighter";
import Dynamic from "@/lib/dynamic";
import ComponentSpinner from "@/components/ui/componentSpinner";

// Lazy load wiki editor (pulls in Milkdown/Crepe)
const WikiMarkdownEditor = Dynamic(
  () => import("@/components/wiki/WikiMarkdownEditor").then(mod => ({ default: mod.WikiMarkdownEditor })),
  { loading: () => <ComponentSpinner /> }
);

// ============================================================================
// Extension → Shiki language mapping
// ============================================================================

const EXT_TO_LANGUAGE: Record<string, string> = {
  // JavaScript / TypeScript
  js: "javascript", jsx: "jsx", ts: "typescript", tsx: "tsx",
  mjs: "javascript", cjs: "javascript", mts: "typescript", cts: "typescript",
  // Web
  html: "html", htm: "html", css: "css", scss: "scss", less: "less", svg: "xml",
  // Data
  json: "json", yaml: "yaml", yml: "yaml", toml: "toml", xml: "xml",
  // Shell
  sh: "bash", bash: "bash", zsh: "bash", fish: "fish",
  // Systems
  rs: "rust", go: "go", c: "c", h: "c", cpp: "cpp", hpp: "cpp",
  java: "java", kt: "kotlin", swift: "swift", m: "objective-c",
  // Scripting
  py: "python", rb: "ruby", php: "php", lua: "lua", pl: "perl",
  // .NET
  cs: "csharp", fs: "fsharp", vb: "vb",
  // Config / DevOps
  dockerfile: "dockerfile", tf: "hcl", sql: "sql",
  graphql: "graphql", gql: "graphql", prisma: "prisma",
  // Misc
  r: "r", dart: "dart", ex: "elixir", exs: "elixir", erl: "erlang",
  zig: "zig", nim: "nim", vue: "vue", svelte: "svelte",
  // Markdown
  md: "markdown", markdown: "markdown",
};

type RenderMode = "plain" | "code" | "markdown";

function detectMode(fileName: string): { mode: RenderMode; language: string | null } {
  const ext = fileName.includes(".")
    ? fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase()
    : "";
  const language = EXT_TO_LANGUAGE[ext] ?? null;

  if (language === "markdown") return { mode: "markdown", language: "markdown" };
  if (language) return { mode: "code", language };
  return { mode: "plain", language: null };
}

// ============================================================================
// Sub-renderers
// ============================================================================

const PlainTextRenderer: FC<{ content: string }> = ({ content }) => (
  <pre className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap break-words font-mono max-w-full p-4">
    {content}
  </pre>
);

const CodeRenderer: FC<{ content: string; language: string }> = ({ content, language }) => {
  const html = useShikiHighlighter(content, language);

  if (!html) {
    return <PlainTextRenderer content={content} />;
  }

  return (
    <div
      className="shiki-container text-sm [&_pre]:p-4"
      style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const MarkdownRenderer: FC<{ content: string }> = ({ content }) => (
  <div className="px-4 py-2">
    <div className="wiki-readonly pointer-events-none select-text [&_.milkdown-toolbar]:hidden [&_button[title*='raw']]:hidden [&_.milkdown-block-handle]:hidden [&_.block-menu]:hidden [&_.ProseMirror]:!pl-0">
      <WikiMarkdownEditor
        initialMarkdown={content}
        className="flex-1 flex flex-col"
        editorClassName="overflow-visible"
      />
    </div>
  </div>
);

// ============================================================================
// Mode selector
// ============================================================================

const ModeSelector: FC<{
  mode: RenderMode;
  language: string | null;
  onChange: (mode: RenderMode) => void;
}> = ({ mode, language, onChange }) => (
  <div className="flex items-center gap-2 shrink-0 px-3 py-1.5 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 text-xs text-zinc-500 dark:text-zinc-400">
    <span>View as:</span>
    <div className="relative">
      <select
        value={mode}
        onChange={(e) => onChange(e.target.value as RenderMode)}
        className="appearance-none bg-white dark:bg-zinc-700 border border-zinc-300 dark:border-zinc-600 rounded pl-2 pr-6 py-1 text-xs text-zinc-700 dark:text-zinc-300 cursor-pointer outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="plain">Plain Text</option>
        <option value="code">Code{language ? ` (${language})` : ""}</option>
        <option value="markdown">Markdown</option>
      </select>
      <ChevronDownIcon className="w-3.5 h-3.5 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-400" />
    </div>
  </div>
);

// ============================================================================
// Main component
// ============================================================================

type TextViewerProps = {
  fileId: string;
  fileName: string;
};

export const TextViewer: FC<TextViewerProps> = ({ fileId, fileName }) => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const detected = useMemo(() => detectMode(fileName), [fileName]);
  const [mode, setMode] = useState<RenderMode>(detected.mode);
  const [language] = useState<string | null>(detected.language);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`/api/file/${fileId}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load file: ${res.statusText}`);
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        if ((err as Error).name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Failed to load file");
        setLoading(false);
      });

    return () => controller.abort();
  }, [fileId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  const text = content ?? "";

  return (
    <div className="flex flex-col h-full w-full">
      <ModeSelector mode={mode} language={language} onChange={setMode} />
      <div className="flex-1 min-h-0 overflow-auto bg-white dark:bg-zinc-900">
        {mode === "markdown" ? (
          <MarkdownRenderer content={text} />
        ) : mode === "code" && language ? (
          <CodeRenderer content={text} language={language} />
        ) : (
          <PlainTextRenderer content={text} />
        )}
      </div>
    </div>
  );
};
